import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './WeeklyReview.css';

interface ReviewSummary {
  id: number;
  week_start: string;
  compliance_pct: number;
  week_tss: number;
  avg_rpe: number;
  ctl_start: number;
  ctl_end: number;
  atl_start: number;
  atl_end: number;
  generated_at: string;
}

interface ReviewDetail extends ReviewSummary {
  ai_analysis: string | null;
  key_observations: string[] | null;
  adjustments_made: string[] | null;
  weakness_snapshot: any;
  planned_summary: any;
  actual_summary: any;
}

interface WeeklyReviewProps {
  isOpen: boolean;
  onClose: () => void;
  onDiscuss: (reviewContext: string) => void;
  onGenerateWeek: () => void;
}

const CloseIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronLeft: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

function formatWeekRange(weekStart: string): string {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', opts)}`;
}

function formatDelta(start: number, end: number): string {
  const delta = end - start;
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}`;
}

const WeeklyReview: React.FC<WeeklyReviewProps> = ({ isOpen, onClose, onDiscuss, onGenerateWeek }) => {
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  const [currentReview, setCurrentReview] = useState<ReviewDetail | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchReviewDetail = useCallback(async (id: number | 'latest') => {
    try {
      const url = id === 'latest'
        ? `${API_BASE_URL}/api/ai-coach/weekly-reviews/latest`
        : `${API_BASE_URL}/api/ai-coach/weekly-reviews/${id}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCurrentReview(data);
      }
    } catch (e) {
      console.error('Failed to fetch review detail:', e);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/ai-coach/weekly-reviews`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.reviews || []);
          setReviews(list);
          setCurrentIndex(0);
          if (list.length > 0) {
            await fetchReviewDetail('latest');
          } else {
            setCurrentReview(null);
          }
        }
      } catch (e) {
        console.error('Failed to fetch reviews:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, fetchReviewDetail]);

  const navigatePrev = async () => {
    if (currentIndex >= reviews.length - 1) return;
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    await fetchReviewDetail(reviews[newIndex].id);
  };

  const navigateNext = async () => {
    if (currentIndex <= 0) return;
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    if (newIndex === 0) {
      await fetchReviewDetail('latest');
    } else {
      await fetchReviewDetail(reviews[newIndex].id);
    }
  };

  const handleDiscuss = () => {
    if (!currentReview) return;
    const observations = currentReview.key_observations?.join(', ') || 'none';
    const context = `Let's discuss my week of ${formatWeekRange(currentReview.week_start)}. Compliance: ${currentReview.compliance_pct}%, TSS: ${currentReview.week_tss}, key observations: ${observations}`;
    onDiscuss(context);
  };

  const handleGenerateWeek = () => {
    onGenerateWeek();
  };

  if (!isOpen) return null;

  return (
    <div className="weekly-review-backdrop" onClick={onClose}>
      <div className={`weekly-review-panel ${isOpen ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="weekly-review-header">
          <h3>Weekly Reviews</h3>
          <button className="weekly-review-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="weekly-review-content">
          {loading ? (
            <div className="weekly-review-loading">Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div className="weekly-review-empty">
              <p>No weekly reviews yet.</p>
              <p>Reviews are generated automatically each Sunday.</p>
            </div>
          ) : currentReview ? (
            <div className="weekly-review-card">
              <div className="review-card-header">
                <span className="review-week-label">
                  Week of {formatWeekRange(currentReview.week_start)}
                </span>
                <span className="review-compliance-badge">
                  {currentReview.compliance_pct}%
                </span>
              </div>

              <div className="review-compliance-bar">
                <div className="review-compliance-track">
                  <div
                    className="review-compliance-fill"
                    style={{ width: `${Math.min(currentReview.compliance_pct, 100)}%` }}
                  />
                </div>
                <span className="review-compliance-label">Compliance</span>
              </div>

              <div className="review-load-summary">
                <h4>Load Summary</h4>
                <div className="review-metrics-grid">
                  <div className="review-metric">
                    <span className="review-metric-label">TSS</span>
                    <span className="review-metric-value">{currentReview.week_tss}</span>
                  </div>
                  <div className="review-metric">
                    <span className="review-metric-label">Avg RPE</span>
                    <span className="review-metric-value">{currentReview.avg_rpe?.toFixed(1) ?? '-'}</span>
                  </div>
                  <div className="review-metric">
                    <span className="review-metric-label">CTL</span>
                    <span className="review-metric-value">
                      {currentReview.ctl_end?.toFixed(0) ?? '-'}
                      <span className="review-metric-delta">
                        {currentReview.ctl_start != null && currentReview.ctl_end != null
                          ? ` (${formatDelta(currentReview.ctl_start, currentReview.ctl_end)})`
                          : ''}
                      </span>
                    </span>
                  </div>
                  <div className="review-metric">
                    <span className="review-metric-label">ATL</span>
                    <span className="review-metric-value">
                      {currentReview.atl_end?.toFixed(0) ?? '-'}
                      <span className="review-metric-delta">
                        {currentReview.atl_start != null && currentReview.atl_end != null
                          ? ` (${formatDelta(currentReview.atl_start, currentReview.atl_end)})`
                          : ''}
                      </span>
                    </span>
                  </div>
                  <div className="review-metric">
                    <span className="review-metric-label">TSB</span>
                    <span className="review-metric-value">
                      {currentReview.ctl_end != null && currentReview.atl_end != null
                        ? (currentReview.ctl_end - currentReview.atl_end).toFixed(0)
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="review-section">
                <h4>AI Observations</h4>
                {currentReview.key_observations && currentReview.key_observations.length > 0 ? (
                  <ul className="review-observations">
                    {currentReview.key_observations.map((obs, i) => (
                      <li key={i}>{obs}</li>
                    ))}
                  </ul>
                ) : currentReview.ai_analysis ? (
                  <p className="review-analysis">{currentReview.ai_analysis}</p>
                ) : (
                  <p className="review-analysis-empty">No AI analysis available for this week.</p>
                )}
              </div>

              <div className="review-section">
                <h4>Adjustments</h4>
                {currentReview.adjustments_made && currentReview.adjustments_made.length > 0 ? (
                  <ul className="review-adjustments">
                    {currentReview.adjustments_made.map((adj, i) => (
                      <li key={i}>{adj}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="review-no-adjustments">No mid-week adjustments</p>
                )}
              </div>

              <div className="review-actions">
                <button className="review-btn-discuss" onClick={handleDiscuss}>
                  Discuss with coach
                </button>
                <button className="review-btn-generate" onClick={handleGenerateWeek}>
                  Generate next week
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {reviews.length > 1 && (
          <div className="weekly-review-nav">
            <button
              className="review-nav-btn"
              onClick={navigatePrev}
              disabled={currentIndex >= reviews.length - 1}
            >
              <ChevronLeft /> Older
            </button>
            <span className="review-nav-position">
              {currentIndex + 1} / {reviews.length}
            </span>
            <button
              className="review-nav-btn"
              onClick={navigateNext}
              disabled={currentIndex <= 0}
            >
              Newer <ChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyReview;
